import dayjs from 'dayjs';
import { useMemo } from 'react';
import * as React from 'react';

import DateSelector from '@/components/elements/DateSelector';
import DateHeader from '@/components/layouts/Schedule/Calendar/DateHeader';
import TableData from '@/components/layouts/Schedule/Calendar/TableData';
import ScheduleContentSelector, {
  ScheduleContentType,
} from '@/components/layouts/Schedule/ContentSelector';
import {
  MultiraceProgram,
  MultiraceType,
  RaceProgram,
  TimeFrame,
} from '@/lib/apollo/types/gen/graphql';
import {
  areas,
  getVelodromeByCode,
  velodromeAreasMap,
  velodromeGroupMap,
} from '@/lib/race';
import { Area, Group } from '@/modelTypes';

import { Headline } from '../../style';
import { SelectorWrapper } from '../style';
import {
  CancelColor,
  Content,
  DateSelectorWrapper,
  GuideArea,
  Note,
  Table,
  TableBodyTitle,
  TableHeadTitle,
  TableTitle,
  TableWrapper,
  Wrapper,
} from './style';

interface CalendarTemplateProps {
  date: dayjs.Dayjs;
  racePrograms: RaceProgram[];
  multiracePrograms: MultiraceProgram[];
}

export type RaceProgramData = {
  velodromeCode?: number;
  isFirstDay: boolean;
  gradeName: string;
  timeFrame: TimeFrame;
  isGirls: boolean;
  isCanceled?: boolean;
  hasChariloto?: boolean;
  hasChariloto3?: boolean;
};

type RaceProgramDataList = {
  [date: string]: RaceProgramData; // date: 2021-01-01
};

type VelodromeData = {
  [velodromeCode in number]: RaceProgramDataList;
};

type SingleraceCalendarData = {
  [area in Area]: VelodromeData;
};

type CharilotoCalendarData = {
  [group in Group]: RaceProgramDataList;
};

const CalendarTemplate: React.FC<CalendarTemplateProps> = ({
  date,
  racePrograms,
  multiracePrograms,
}) => {
  const dateList = useMemo(
    () =>
      [...Array(date.daysInMonth())].map((_, i) =>
        date.startOf('month').date(i + 1),
      ),
    [date],
  );

  const singleraceCalendarData = useMemo(() => {
    const scd = {} as SingleraceCalendarData;
    Object.keys(velodromeAreasMap).forEach(area => {
      scd[area as Area] = {} as VelodromeData;
      velodromeAreasMap[area as Area].forEach(velodrome => {
        scd[area as Area][velodrome.code] = {} as RaceProgramDataList;
        const filteredRacePrograms = racePrograms.filter(
          rp => rp.velodromeCode === velodrome.code,
        );
        dateList.forEach(d => {
          const day = d.format('YYYY-MM-DD');
          scd[area as Area][velodrome.code][day] = {} as RaceProgramData;
          filteredRacePrograms.forEach(rp => {
            if (rp.heldDate.isSame(d)) {
              scd[area as Area][velodrome.code][day] = {
                isFirstDay:
                  d.isSame(date.startOf('month')) || rp.dailyNum === 1,
                gradeName: rp.gradeName,
                timeFrame: rp.timeFrame,
                isGirls: rp.isGirls,
                isCanceled: rp.isCanceled,
              } as RaceProgramData;
            }
          });
        });
      });
    });
    return scd;
  }, [date]);

  const charilotoCalendarData = useMemo(() => {
    const ccd = {} as CharilotoCalendarData;
    Object.keys(velodromeGroupMap).forEach(group => {
      ccd[group as Group] = {} as RaceProgramDataList;
      dateList.forEach(d => {
        const day = d.format('YYYY-MM-DD');
        ccd[group as Group][day] = {} as RaceProgramData;
        const fmp = multiracePrograms.filter(
          mp =>
            [
              MultiraceType.Chariloto,
              MultiraceType.Chariloto5,
              MultiraceType.CharilotoSelect,
            ].includes(mp.multiraceType) &&
            velodromeGroupMap[group as Group]
              .map(v => v.code)
              .includes(mp.velodromeCode) &&
            mp.heldDate.isSame(d),
        );
        if (fmp.length > 0) {
          const raceProgram = racePrograms.filter(
            rp => rp.id === fmp[0].raceProgramId,
          )[0];
          ccd[group as Group][day] = {
            velodromeCode: fmp[0].velodromeCode,
            isFirstDay:
              multiracePrograms
                .filter(
                  tmp =>
                    tmp.multiraceType !== MultiraceType.Chariloto3 &&
                    tmp.velodromeCode === fmp[0].velodromeCode &&
                    tmp.heldFirstDate.isSame(fmp[0].heldFirstDate),
                )
                .findIndex(tmp =>
                  tmp.heldDate.isBefore(fmp[0].heldDate, 'day'),
                ) < 0,
            gradeName: raceProgram.gradeName,
            timeFrame: raceProgram.timeFrame,
            isGirls: raceProgram.isGirls,
            hasChariloto:
              multiracePrograms.findIndex(
                tmp =>
                  tmp.multiraceType === MultiraceType.Chariloto &&
                  tmp.velodromeCode === fmp[0].velodromeCode &&
                  tmp.heldFirstDate.isSame(fmp[0].heldFirstDate),
              ) >= 0,
            hasChariloto3:
              multiracePrograms.findIndex(
                tmp =>
                  tmp.multiraceType === MultiraceType.Chariloto3 &&
                  tmp.velodromeCode === fmp[0].velodromeCode &&
                  tmp.heldFirstDate.isSame(fmp[0].heldFirstDate),
              ) >= 0,
          } as RaceProgramData;
        }
      });
    });
    return ccd;
  }, [date]);

  const dokantoCalendarData = useMemo(() => {
    const dcd = {} as RaceProgramDataList;
    dateList.forEach(d => {
      const day = d.format('YYYY-MM-DD');
      dcd[day] = {} as RaceProgramData;
      const fmp = multiracePrograms.filter(
        mp =>
          [MultiraceType.Dokanto7, MultiraceType.Dokanto4two].includes(
            mp.multiraceType,
          ) && mp.heldDate.isSame(d),
      );
      if (fmp.length > 0) {
        const raceProgram = racePrograms.filter(
          rp => rp.id === fmp[0].raceProgramId,
        )[0];
        dcd[day] = {
          velodromeCode: fmp[0].velodromeCode,
          isFirstDay:
            d.isSame(date.startOf('month')) || raceProgram.dailyNum === 1,
          gradeName: raceProgram.gradeName,
          timeFrame: raceProgram.timeFrame,
          isGirls: raceProgram.isGirls,
        } as RaceProgramData;
      }
    });
    return dcd;
  }, [date]);

  return (
    <>
      <Headline>開催スケジュール</Headline>
      <SelectorWrapper>
        <ScheduleContentSelector
          scheduleContentType={ScheduleContentType.Calendar}
        />
      </SelectorWrapper>
      <DateSelectorWrapper>
        <DateSelector
          date={date}
          displayFormat="YYYY年M月"
          pathFormat="YYYY-MM"
          unit="month"
        />
      </DateSelectorWrapper>
      <Wrapper>
        <GuideArea>
          <CancelColor />
          <div>開催中止</div>
        </GuideArea>
        <Content>
          {Object.keys(singleraceCalendarData).map(areaRomanName => (
            <div key={`area-${areaRomanName}`}>
              <TableTitle>{areas[areaRomanName as Area]}</TableTitle>
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <TableHeadTitle>競輪場</TableHeadTitle>
                      <DateHeader dateList={dateList} />
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(
                      singleraceCalendarData[areaRomanName as Area],
                    ).map(velodromeCode => (
                      <tr key={`velodrome-${velodromeCode}`}>
                        <TableBodyTitle>
                          {getVelodromeByCode(Number(velodromeCode)).name}
                        </TableBodyTitle>
                        {Object.keys(
                          singleraceCalendarData[areaRomanName as Area][
                            Number(velodromeCode)
                          ],
                        ).map(day => (
                          <TableData
                            key={`velodrome-${day}`}
                            date={date}
                            day={day}
                            raceProgramData={
                              singleraceCalendarData[areaRomanName as Area][
                                Number(velodromeCode)
                              ][day]
                            }
                          />
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            </div>
          ))}
        </Content>
      </Wrapper>
      <Wrapper>
        <TableTitle>チャリロト</TableTitle>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <TableHeadTitle>グループ</TableHeadTitle>
                <DateHeader dateList={dateList} />
              </tr>
            </thead>
            <tbody>
              {Object.keys(charilotoCalendarData).map(group => (
                <tr key={`velodrome-${group}`}>
                  <TableBodyTitle>{group}</TableBodyTitle>
                  {Object.keys(charilotoCalendarData[group as Group]).map(
                    day => (
                      <TableData
                        key={`velodrome-${day}`}
                        date={date}
                        day={day}
                        raceProgramData={
                          charilotoCalendarData[group as Group][day]
                        }
                      />
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
        <Note>
          <div>※1</div>
          <div>
            Dokanto!発売に伴い、チャリロトの発売はございません。チャリロトセレクト、チャリロト5、チャリロト3（※2の開催場は除く）
            は発売しております。
          </div>
          <div>※2</div>
          <div>チャリロト3の発売はありません。</div>
        </Note>
      </Wrapper>
      <Wrapper>
        <TableTitle>Dokanto!</TableTitle>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <TableHeadTitle />
                <DateHeader dateList={dateList} />
              </tr>
            </thead>
            <tbody>
              <tr>
                <TableBodyTitle>
                  対象
                  <br />
                  レース
                </TableBodyTitle>
                {Object.keys(dokantoCalendarData).map(day => (
                  <TableData
                    key={`velodrome-${day}`}
                    date={date}
                    day={day}
                    raceProgramData={dokantoCalendarData[day]}
                  />
                ))}
              </tr>
            </tbody>
          </Table>
        </TableWrapper>
      </Wrapper>
    </>
  );
};

export default CalendarTemplate;
